import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { useToast } from "@src/appV2/lib";
import { type NfcTagRequest } from "@src/appV2/Shifts/Shift/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { useSkippableWait } from "../../../utils/useSkippableWait";
import { getWorkerShiftPath } from "../../api/useGetWorkerShift";
import {
  ActionCheckType,
  ShiftStage,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { NfcHashValidationAction } from "../NfcValidation/api/types";
import { WorkerNfcValidation } from "../NfcValidation/WorkerNfcValidation";
import { BreakActionConfirmation } from "./BreakActionConfirmation";
import { DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS } from "./constants";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";

export interface BreakActionBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  shiftStage: ShiftStage.BREAK_START | ShiftStage.BREAK_END | ShiftStage.SKIP_BREAK;
  workerId: string;
  workplaceId: string;
  workplaceName: string;
  isNfcEnabled: boolean;
  nfcTagRequests: NfcTagRequest[];
  isMissedPunchRequestEnabled: boolean;
}

type BreakActionBottomSheetStep =
  | "break-action-confirm"
  | "nfc-validation"
  | "requesting-break-action"
  | "break-action-success";

function getActionConfig(
  shiftStage: ShiftStage.BREAK_START | ShiftStage.BREAK_END | ShiftStage.SKIP_BREAK
) {
  switch (shiftStage) {
    case ShiftStage.BREAK_START: {
      return {
        action: "break-start" as const,
        loadingTitle: "Confirming your break start",
        successTitle: "Break started",
        successDescription: "You can now take your break",
        errorMessage: "Failed to start break, please try again.",
      };
    }

    case ShiftStage.SKIP_BREAK: {
      return {
        action: "break-skip" as const,
        loadingTitle: "Confirming your break skip",
        successTitle: "Break skipped",
        successDescription: "You have skipped your break",
        errorMessage: "Failed to skip break, please try again.",
      };
    }

    case ShiftStage.BREAK_END: {
      return {
        action: "break-end" as const,
        loadingTitle: "Confirming your break end",
        successTitle: "Break ended",
        successDescription: "You can now return to your shift",
        errorMessage: "Failed to end break, please try again.",
      };
    }

    default: {
      throw new Error(`Invalid shift state`);
    }
  }
}

function getNfcClockAction(
  shiftStage: ShiftStage.BREAK_START | ShiftStage.BREAK_END | ShiftStage.SKIP_BREAK
): NfcHashValidationAction {
  switch (shiftStage) {
    case ShiftStage.SKIP_BREAK: {
      return NfcHashValidationAction.SKIP_BREAK;
    }

    case ShiftStage.BREAK_START: {
      return NfcHashValidationAction.BREAK_START;
    }

    case ShiftStage.BREAK_END: {
      return NfcHashValidationAction.BREAK_END;
    }

    default: {
      return shiftStage satisfies never;
    }
  }
}

export function BreakActionBottomSheet(props: BreakActionBottomSheetProps) {
  const {
    modalState,
    shiftId,
    workerId,
    workplaceId,
    workplaceName,
    isNfcEnabled,
    shiftStage,
    nfcTagRequests,
    isMissedPunchRequestEnabled,
  } = props;

  const queryClient = useQueryClient();
  const [step, setStep] = useState<BreakActionBottomSheetStep>("break-action-confirm");

  const { mutateAsync: recordTimekeepingAction } = useRecordShiftTimekeepingAction();

  const { showErrorToast } = useToast();

  const { wait, skip: skipWait } = useSkippableWait();

  const actionConfig = getActionConfig(shiftStage);

  const closeBottomSheet = useCallback(() => {
    skipWait();
    modalState.closeModal();
    setStep("break-action-confirm");
  }, [modalState, skipWait]);

  const onBreakActionSuccess = useCallback(async () => {
    setStep("break-action-success");
    await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);
    closeBottomSheet();
  }, [closeBottomSheet, wait]);

  const recordBreakAction = useCallback(async () => {
    try {
      await recordTimekeepingAction({
        shiftId,
        stage: shiftStage,
        shiftActionCheck: isNfcEnabled ? ActionCheckType.NFC : ActionCheckType.LOCATION,
        verificationMethod: isNfcEnabled
          ? AttendanceVerificationMethod.NFC_TAG
          : AttendanceVerificationMethod.GEOFENCE,
      });

      await queryClient.invalidateQueries({
        queryKey: [getWorkerShiftPath(shiftId)],
      });

      await onBreakActionSuccess();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : actionConfig.errorMessage;
      showErrorToast(errorMessage);
      closeBottomSheet();
    }
  }, [
    recordTimekeepingAction,
    shiftId,
    shiftStage,
    queryClient,
    onBreakActionSuccess,
    actionConfig.errorMessage,
    showErrorToast,
    closeBottomSheet,
    isNfcEnabled,
  ]);

  function onBreakActionConfirmed() {
    setStep("requesting-break-action");
    void recordBreakAction();
  }

  // check TX-2987 for reasoning as to why we're simply blocking the action in case of NFC scan failure
  const onNfcFailed = useCallback(() => {
    showErrorToast(actionConfig.errorMessage);
    closeBottomSheet();
  }, [showErrorToast, closeBottomSheet, actionConfig.errorMessage]);

  return (
    <BottomSheet modalState={modalState} canDismiss={false}>
      {step === "break-action-confirm" && (
        <BreakActionConfirmation
          modalState={modalState}
          action={actionConfig.action}
          onConfirm={() => {
            if (isNfcEnabled) {
              setStep("nfc-validation");
            } else {
              onBreakActionConfirmed();
            }
          }}
          onCancel={closeBottomSheet}
        />
      )}
      {step === "nfc-validation" && (
        <WorkerNfcValidation
          disableSkip
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          shiftId={shiftId}
          metaProps={{ workerId, workplaceId, workplaceName }}
          nfcTagRequests={nfcTagRequests}
          clockAction={getNfcClockAction(shiftStage)}
          onSuccess={() => {
            onBreakActionConfirmed();
          }}
          onCancel={closeBottomSheet}
          onSkipLocationCheck={onNfcFailed}
          onNfcNotSupported={onNfcFailed}
        />
      )}
      {step === "requesting-break-action" && (
        <BottomSheetLoadingContent
          title={actionConfig.loadingTitle}
          description="Should only take a few seconds"
        />
      )}
      {step === "break-action-success" && (
        <TimekeepingActionSuccessContent
          title={actionConfig.successTitle}
          description={actionConfig.successDescription}
          onClose={closeBottomSheet}
        />
      )}
    </BottomSheet>
  );
}
